<template>
    <div>
        <!-- Mobilos overlay - külön elem -->
        <div class="mobile-overlay" v-if="isMobile && isMobileOpen" @click="closeMobileMenu"></div>
        
        <aside class="dashboard-sidebar" :class="{ 
            'collapsed': !is_sidebar_visible && !isMobile,
            'mobile-open': isMobile && isMobileOpen,
            'loading': isLoading
        }">
            <div class="sidebar-content">
                <!-- Logo -->
                <div class="sidebar-logo">
                    <router-link to="/fooldal" class="logo-link">
                        <img src="/images/rr-fulw.png" alt="Logo" class="header-logo" />
                    </router-link>
                </div>

                <!-- Navigáció -->
                <nav class="sidebar-nav">
                    <ul class="nav-list">
                        <li class="nav-item">
                            <router-link to="/fooldal" class="nav-link" 
                                       :title="!is_sidebar_visible && !isMobileOpen ? 'Kezdőoldal' : ''"
                                       @click="handleNavClick">
                                <i class="fas fa-house"></i>
                                <span v-if="is_sidebar_visible || (isMobile && isMobileOpen)">Kezdőoldal</span>
                            </router-link>
                        </li>
                        <li class="nav-item">
                            <router-link to="/projects" class="nav-link" 
                                       :title="!is_sidebar_visible && !isMobileOpen ? 'Projektek' : ''"
                                       @click="handleNavClick">
                                <i class="fas fa-project-diagram"></i>
                                <span v-if="is_sidebar_visible || (isMobile && isMobileOpen)">Projektek</span>
                            </router-link>
                        </li>
                        <li class="nav-item" >
                            <router-link to="/invoices" class="nav-link" 
                                       :title="!is_sidebar_visible && !isMobileOpen ? 'Számlák' : ''"
                                       @click="handleNavClick">
                                <i class="fas fa-file-invoice"></i>
                                <span v-if="is_sidebar_visible || (isMobile && isMobileOpen)">Számlák</span>
                            </router-link>
                        </li>
                        <li class="nav-item">
                            <router-link to="/settings/me" class="nav-link" 
                                       :title="!is_sidebar_visible && !isMobileOpen ? 'Profil beállítások' : ''"
                                       @click="handleNavClick">
                                <i class="fas fa-user-cog"></i>
                                <span v-if="is_sidebar_visible || (isMobile && isMobileOpen)" class="nav-link__label">
                                    Profil
                                    <FeatureFlag feature="PROFILE_INFO" :defaultEnabled="true">
                                        <span class="feature-badge">Új</span>
                                    </FeatureFlag>
                                </span>
                            </router-link>
                        </li>
                    </ul>
                </nav>

                <!-- Alsó rész -->
                <div class="sidebar-footer">
                    <div class="user-info">
                        <img src="/images/user_empty.png" alt="User" class="user-img">
                        <div class="user-details" v-if="is_sidebar_visible || (isMobile && isMobileOpen)">
                            <div class="user-name">{{ partner.company_name ?? partner.full_name }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </aside>
    </div>
</template>

<script>
import authService from '@/services/authService';
import apiService from '@/services/apiService';
import axios from 'axios';
import FeatureFlag from '@/components/FeatureFlag.vue';

export default {
    components: {
        FeatureFlag
    },
    props: {
        is_sidebar_visible: {
            type: Boolean,
            required: true
        }
    },
    name: 'DashboardSidebar',
    data() {
        return {
            partner: {},
            isMobile: false,
            isMobileOpen: false,
            isLoading: false,
            requestInterceptor: null,
            responseInterceptor: null
        }
    },
    
    mounted() {
        const user = authService.getUser() || {};
        this.partner = user.client || user.company || {};
        this.checkMobile();
        window.addEventListener('resize', this.checkMobile);
        
        // Listen for mobile toggle events
        document.addEventListener('toggle-mobile-sidebar', this.toggleMobileMenu);
        
        // Listen for loading state changes
        this.setupLoadingListener();
    },
    
    beforeUnmount() {
        window.removeEventListener('resize', this.checkMobile);
        document.removeEventListener('toggle-mobile-sidebar', this.toggleMobileMenu);
        
        // Cleanup interceptors
        if (this.requestInterceptor !== null) {
            axios.interceptors.request.eject(this.requestInterceptor);
        }
        if (this.responseInterceptor !== null) {
            axios.interceptors.response.eject(this.responseInterceptor);
        }
    },
    computed: {
        isTester() {
            return authService.isTester();
        }
    },
    
    methods: {
        checkMobile() {
            const wasMobile = this.isMobile;
            this.isMobile = window.innerWidth <= 768;
            
            // Ha desktopról mobilra váltunk, zárjuk be a mobilos menüt
            if (wasMobile && !this.isMobile) {
                this.isMobileOpen = false;
            }
        },
        
        toggleMobileMenu() {
            if (this.isMobile) {
                this.isMobileOpen = !this.isMobileOpen;
            }
        },
        
        closeMobileMenu() {
            this.isMobileOpen = false;
        },
        
        handleNavClick() {
            // Mobilon bezárjuk a menüt navigáció után
            if (this.isMobile) {
                this.isMobileOpen = false;
            }
        },
        
        setupLoadingListener() {
            // Request interceptor - loading starts
            this.requestInterceptor = axios.interceptors.request.use(
                config => {
                    this.isLoading = true;
                    return config;
                },
                error => {
                    this.isLoading = false;
                    return Promise.reject(error);
                }
            );
            
            // Response interceptor - loading ends
            this.responseInterceptor = axios.interceptors.response.use(
                response => {
                    this.isLoading = false;
                    return response;
                },
                error => {
                    this.isLoading = false;
                    return Promise.reject(error);
                }
            );
        }
    }
}
</script>

<style scoped>
/* Mobilos overlay */
.mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
}

.dashboard-sidebar {
    width: 250px;
    background: #252429;
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    z-index: 999;
    transition: width 0.3s ease, transform 0.3s ease;
    box-shadow: 5px 0 10px rgba(41,74,150,0.5);
}

.dashboard-sidebar.collapsed {
    width: 60px;
}

/* Loading pulzáló effekt */
.dashboard-sidebar.loading {
    animation: shadowPulse 1.5s ease-in-out infinite;
}

@keyframes shadowPulse {
    0%, 100% {
        box-shadow: 5px 0 10px rgba(41,74,150,0.5);
    }
    50% {
        box-shadow: 5px 0 20px rgba(41,74,150,0.8);
    }
}

.sidebar-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px 0;
    background: #252429;
}

/* Logo */
.sidebar-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px 30px 20px;

}

.collapsed .sidebar-logo {
    padding: 0 10px 30px 10px;
}

.logo-link {
    display: block;
    width: 100%;
    text-align: center;
}

.header-logo {
    width: 80%;
    object-fit: contain;
    display: block;
    margin: 0 auto;
    max-width: 100%;
}

.collapsed .header-logo {
    height: 35px;
}

/* Navigáció */
.sidebar-nav {
    flex: 1;
}

.nav-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.nav-item {
    margin-bottom: 4px;
}

.nav-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    color: #b8b8b8;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    border-right: 3px solid transparent;
}

.collapsed .nav-link {
    justify-content: center;
    padding: 12px 10px;
}

.nav-link:hover {
    background-color: #2f2d34;
    color: #ffffff;
}

.nav-link.router-link-active {
    background-color: #3a4d6b;
    color: #ffffff;
    border-right-color: #4a90e2;
}

.nav-link i {
    width: 20px;
    text-align: center;
    font-size: 16px;
    flex-shrink: 0;
}

.nav-link__label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.feature-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 9px;
    border-radius: 999px;
    border: 1px solid rgba(74, 144, 226, 0.55);
    background: rgba(74, 144, 226, 0.18);
    color: #dbeafe;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    box-shadow: 0 2px 8px rgba(74, 144, 226, 0.2);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.nav-link:hover .feature-badge {
    background: rgba(74, 144, 226, 0.28);
    border-color: rgba(74, 144, 226, 0.75);
    transform: translateY(-1px);
}

/* Alsó rész */
.sidebar-footer {
    padding: 20px;
    border-top: 1px solid #3a3841;
}

.collapsed .sidebar-footer {
    padding: 20px 10px;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.collapsed .user-info {
    justify-content: center;
}

.user-img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
}

.user-details {
    flex: 1;
}

.user-name {
    font-size: 14px;
    font-weight: 500;
    color: #ffffff;
    margin-bottom: 2px;
}

.user-status {
    font-size: 12px;
    color: #28a745;
}

/* Mobilos stílusok */
@media (max-width: 768px) {
    .dashboard-sidebar {
        width: 280px;
        height: 100vh;
        top: 0;
        transform: translateX(-100%);
        z-index: 1001;
        box-shadow: 5px 0 10px rgba(41,74,150,0.5);
    }
    
    .dashboard-sidebar.mobile-open {
        transform: translateX(0);
    }
    
    .sidebar-logo {
        padding: 20px;
    }
    
    .header-logo {
        height: 50px;
    }
    
    .nav-link {
        padding: 16px 20px;
        font-size: 16px;
    }
    
    .nav-link i {
        width: 24px;
        font-size: 18px;
    }
}
</style>
